import {Module, Store} from "vuex"
import {AppState, RemoteData, RemoteDataError} from "@/store"
import logger from "@/utils/logging"
import apiService from "@/services/api.service"
import {Product, ProductList, ProductUpdate} from "@/services/models.types"
import {Vue} from "vue-property-decorator"

export interface ProductsState {
    [id: string]: RemoteData<ProductList>
}

export const namespace = "products"

const getters = {
    GET_LIST_PRODUCTS: "getProductsInList",
    IS_LIST_FETCH_IN_PROGRESS: "isDataLoading",
}

const actions = {
    FETCH_LIST: "fetchList",
}

const mutations = {
    FETCH_LIST_START: "fetchListStart",
    FETCH_LIST_SAVE: "fetchListSave",
    FETCH_LIST_ERROR: "fetchListError",
}

export const module: Module<ProductsState, AppState> = {
    namespaced: true,
    state: {},
    getters: {
        [getters.IS_LIST_FETCH_IN_PROGRESS](state: ProductsState) {
            return (listId: string) => {
                const item = state[listId]
                return item !== undefined && item.isLoading
            }
        },
        [getters.GET_LIST_PRODUCTS](state: ProductsState) {
            return (listId: string) => {
                const productList = state[listId]
                return productList === undefined || productList.data === undefined ? [] : productList.data.products
            }
        },
    },
    mutations: {
        [mutations.FETCH_LIST_START](state: ProductsState, listId: string) {
            const existingItem: RemoteData<ProductList> = state[listId]
            Vue.set<RemoteData<ProductList>>(state, listId, {
                isLoading: true,
                error: null,
                data: existingItem === undefined ? undefined : existingItem.data,
            })
        },
        [mutations.FETCH_LIST_SAVE](state: ProductsState, list: ProductList) {
            Vue.set<RemoteData<ProductList>>(state, list.id, {
                isLoading: false,
                error: null,
                data: list,
            })
        },
        [mutations.FETCH_LIST_ERROR](state: ProductsState, payload: { listId: string, error: Error }) {
            const existingItem: RemoteData<ProductList> = state[payload.listId]
            Vue.set<RemoteData<ProductList>>(state, payload.listId, {
                isLoading: false,
                error: new RemoteDataError(payload.error),
                data: existingItem.data,
            })
        },
    },
    actions: {
        async [actions.FETCH_LIST](context, listId: string) {
            logger.debug(`Fetching product list #${listId}...`)
            context.commit(mutations.FETCH_LIST_START, listId)
            try {
                const list = await apiService.fetchProductList(listId)
                sortListProducts(list.products)
                context.commit(mutations.FETCH_LIST_SAVE, list)
                logger.debug("Fetched product list:", list)
            } catch (error) {
                logger.error("Failed to fetch product list:", error)
                context.commit(mutations.FETCH_LIST_ERROR, {listId, error})
            }
        },
    },
}

/** Since VueX doesn't currently support fully typing things, we can define the signatures by hand. */
export function wrapGetters(store: Store<AppState>) {
    return {
        isListLoading(listId: string): boolean {
            return store.getters[`${namespace}/${getters.IS_LIST_FETCH_IN_PROGRESS}`](listId)
        },
        getProductsInList(listId: string): Product[] {
            return store.getters[`${namespace}/${getters.GET_LIST_PRODUCTS}`](listId)
        },
    }
}

/** Since VueX doesn't currently support fully typing things, we can define the signatures by hand. */
export function wrapActions(store: Store<AppState>) {
    return {
        loadList(listId: string): Promise<void> {
            return store.dispatch(`${namespace}/${actions.FETCH_LIST}`, listId)
        },
    }
}

function sortListProducts(products: Product[]) {
    const culture = "da"

    products
        .sort((a, b) => {
            const aSubcategoryName = (a.subcategory && a.subcategory.categoryName) || "\uffff"
            const bSubcategoryName = (b.subcategory && b.subcategory.categoryName) || "\uffff"

            const subcategoryComparisonResult = aSubcategoryName.localeCompare(bSubcategoryName, culture)
            if (subcategoryComparisonResult !== 0) {
                return subcategoryComparisonResult
            }

            return a.displayName.localeCompare(b.displayName, culture)
        })
}
